import { useRef } from 'react'
import { useGlobalEvent, useGlobalStore } from '@highlight/ipc/react'
import { CalendarEvent, DetectedMeeting, RecallDetectedMeeting } from '@highlight/common'
import MeetingIcon from '@renderer/components/Icons/MeetingIcon'
import { CalendarEventIcon } from '@renderer/components/Icons/CalendarEventIcon'
import { useMeetingSettings } from '@renderer/hooks/settings/useMeetingSettings'
import { useFeaturePrompt } from '@renderer/hooks/useFeaturePrompt'
import { useIsTakingNotes } from '@renderer/hooks/audio/use-audio-note-selectors'
import AudioNoteIpcRenderer from '@renderer/ipc/AudioNoteIpc.renderer'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import HighlightLogoIcon from '@renderer/components/Icons/HighlightLogoIcon'
import { MeetingNotesFeaturePromptToast } from '../toasts/meeting/meeting-notes-feature-prompt-toast'
import { TranscriptionDowngradedToast } from '../toasts/meeting/transcription-downgraded-toast'
import {
  MEETING_LIMIT_REACHED_EVENT,
  MEETING_TRANSCRIPTION_DOWNGRADED_EVENT,
  type MeetingLimitReachedPayload,
} from '@shared/meeting-gating-events'
import { useMeetingLimitRoute } from '@renderer/chat/hooks/use-meeting-limit-route'
import { refreshBilling } from '@renderer/chat/store/global-billing'
import { resolveMeetingRefusalSurface } from '@renderer/chat/utils/billingUtils'
import {
  FIRST_MEETING_AFTER_ONBOARDING_EVENT,
  type FirstMeetingAfterOnboardingPayload,
} from '@shared/onboarding/first-meeting-event'
import { MeetingStartToast } from '../toasts/meeting/meeting-start-toast'
import { MeetingStartingSoonToast } from '../toasts/meeting/meeting-starting-soon-toast'
import { MeetingViewToast } from '../toasts/meeting/meeting-view-toast'
import { WaitingForMeetingToast } from '../toasts/meeting/waiting-for-meeting-toast'
import { RecordingStartedToast } from '../toasts/meeting/recording-started-toast'
import { OutOfMeetingHoursToast } from '../toasts/meeting/out-of-meeting-hours-toast'
import { PlanUnverifiedToast } from '../toasts/meeting/plan-unverified-toast'
import { useCurrentMeetingCalendarEvent } from '../toasts/meeting/hooks'
import { AudioPermissionToast } from '../toasts/meeting/audio-permission-toast'
import {
  NotchAudioPermissionToast,
  NotchMeetingStartToast,
  NotchTranscribingToast,
} from '../toasts/meeting/notch-meeting-toast'
import { NotchMeetingPrepareToast, NotchMeetingStartingSoonToast } from '../toasts/meeting/notch-upcoming-meeting-toast'
import { useToast } from '../use-toast'
import { useTriggerBriefGeneration } from '@renderer/overlay/Sidepanel/components/meeting/brief/use-trigger-brief-generation'
import { useMeetingHoursExhausted } from '@renderer/overlay/Sidepanel/components/meeting/brief/use-meeting-hours-exhausted'
import { useOutOfMeetingHours } from '@renderer/overlay/Sidepanel/components/meeting/use-out-of-meeting-hours'
import { useMeetingBriefGenerationEnabled } from '@renderer/overlay/Sidepanel/components/meeting/brief/use-meeting-brief-generation-enabled'
import { useSetCalendarEventBriefGenerating } from '@renderer/hooks/use-calendar-events'
import type { AudioPermissionRequiredPayload } from '@shared/types/AudioCapturePermissions'
import { getDetectedMeetingAppName, getMeetingDataFromMeeting } from '@shared/utils/audio-notes/AudioNoteUtils'

const getMeetingToastId = (meetingId: string) => `meeting-${meetingId}`
const DETECTED_MEETING_TOAST_ID = 'detected-meeting-active'
const MEETING_DETECTION_COOLDOWN_MS = 60_000

type ToastApi = ReturnType<typeof useToast>

export function useMeetingToasts(toast: ToastApi) {
  const meetingToastDismissedAtRef = useRef<number>(0)
  const { allowMeetingNotifications } = useMeetingSettings()
  const [meetingStartingSoonEnabled] = useGlobalStore('settings', 'notifications.meetingStartingSoon', true)
  const [meetingDetectedEnabled] = useGlobalStore('settings', 'notifications.meetingDetected', true)
  const { promptStatus, shouldShow: shouldShowMeetingNotesFeaturePrompt } = useFeaturePrompt('meeting-notes')
  const isTakingNotes = useIsTakingNotes()
  const triggerBrief = useTriggerBriefGeneration()
  const briefGenerationEnabled = useMeetingBriefGenerationEnabled()
  const briefHoursExhausted = useMeetingHoursExhausted()
  const outOfHours = useOutOfMeetingHours()
  const meetingLimitRoute = useMeetingLimitRoute()
  const currentMeetingCalendarEvent = useCurrentMeetingCalendarEvent()
  const setBriefGenerating = useSetCalendarEventBriefGenerating()

  // Fired by ScheduledMeetingService 15 minutes before a meeting. Sole
  // entry point for brief generation — decoupled from sidepanel state so
  // it kicks off whether or not the user has the cell visible. If the app
  // is launched after the 15-min mark, the user can hit "Generate brief"
  // manually from the Brief tab.
  //
  // Gated by the `meeting-brief-generation` LD flag — this is the only
  // path that spawns brief-generation agents without an explicit user
  // click, so flipping the flag off here is the highest-leverage way to
  // throttle server-side cost. Also gated on the meeting-hours balance the
  // backend refuses new runs on. Unlike the click paths there is no button
  // here to gray out, so an exhausted user simply gets no auto-brief.
  useGlobalEvent('scheduled-meeting:trigger-brief', (meeting: CalendarEvent) => {
    if (!briefGenerationEnabled || briefHoursExhausted) return
    if (!meeting?.id) return
    AmplitudeRenderer.trackEvent('Meeting Brief', {
      action: 'generate',
      source: 'scheduled-trigger',
      meeting_id: meeting.id,
    })
    setBriefGenerating(meeting.id, true)
    triggerBrief.mutate(meeting.id, { onError: () => setBriefGenerating(meeting.id, false) })
  })

  useGlobalEvent('meeting:started-with-transcription', (meeting: DetectedMeeting) => {
    if (!allowMeetingNotifications || !meetingDetectedEnabled) return
    if (Date.now() - meetingToastDismissedAtRef.current < MEETING_DETECTION_COOLDOWN_MS) return

    AmplitudeRenderer.trackEvent('Audio Note Toast', {
      type: 'detected meeting',
      isStarting: true,
      meetingName: meeting.app.name,
    })

    // Fired on the start request, before the gate answers. Out of hours, the
    // start it announces is about to be refused.
    toast.add({
      analytics: { type: 'transcription started', meetingApp: meeting.app.name, outOfHours },
      id: DETECTED_MEETING_TOAST_ID,
      title: outOfHours ? 'Transcription not started' : 'Transcription started',
      icon: <MeetingIcon appName={meeting.app.name} size={16} />,
      duration: outOfHours ? 15000 : 5000,
      autoExpandDelayMs: 0,
      autoCollapseDelayMs: 8000,
      onDismiss: () => {
        meetingToastDismissedAtRef.current = Date.now()
      },
      body: (toastId) => <RecordingStartedToast toastId={toastId} meeting={meeting} />,
      notchBody: () => <NotchTranscribingToast />,
    })
  })

  useGlobalEvent('meeting:started', (meeting: DetectedMeeting | RecallDetectedMeeting) => {
    if (!allowMeetingNotifications || !meetingDetectedEnabled) return
    if (!isTakingNotes && Date.now() - meetingToastDismissedAtRef.current < MEETING_DETECTION_COOLDOWN_MS) return

    if (shouldShowMeetingNotesFeaturePrompt && !isTakingNotes) {
      AmplitudeRenderer.trackEvent('Feature Prompt Toast', {
        type: 'meeting-notes',
        action: 'show',
        status: promptStatus,
      })

      toast.add({
        analytics: { type: 'meeting detected', meetingApp: meeting.app.name, featurePrompt: true },
        id: DETECTED_MEETING_TOAST_ID,
        icon: <MeetingIcon appName={meeting.app.name} size={16} />,
        title: meeting.windowTitle || 'Meeting Detected',
        duration: 15000,
        autoExpandDelayMs: 0,
        autoCollapseDelayMs: 5000,
        onDismiss: () => {
          meetingToastDismissedAtRef.current = Date.now()
        },
        body: (toastId) => <MeetingNotesFeaturePromptToast meeting={meeting} toastId={toastId} />,
        notchBody: (toastId) => <NotchMeetingStartToast meeting={meeting} toastId={toastId} />,
      })
    } else {
      AmplitudeRenderer.trackEvent('Audio Note Toast', {
        type: 'detected meeting',
        isStarting: false,
        meetingName: meeting.app.name,
      })

      toast.add({
        analytics: { type: 'meeting detected', meetingApp: meeting.app.name, featurePrompt: false },
        id: DETECTED_MEETING_TOAST_ID,
        icon: <MeetingIcon appName={meeting.app.name} size={16} />,
        title: meeting.windowTitle || 'Meeting Detected',
        duration: 15000,
        autoExpandDelayMs: 0,
        autoCollapseDelayMs: 5000,
        onDismiss: () => {
          meetingToastDismissedAtRef.current = Date.now()
        },
        body: (toastId) => <MeetingStartToast meeting={meeting} toastId={toastId} />,
        notchBody: (toastId) => <NotchMeetingStartToast meeting={meeting} toastId={toastId} />,
      })
    }
  })

  // Autopilot held off because the user was in this call during onboarding.
  // Not gated by the notification toggles or the detection cooldown: autopilot
  // is on, and this prompt is the only way it asks before the first recording.
  useGlobalEvent(FIRST_MEETING_AFTER_ONBOARDING_EVENT, (meeting: FirstMeetingAfterOnboardingPayload) => {
    if (isTakingNotes) return

    AmplitudeRenderer.trackEvent('Audio Note Toast', {
      type: 'first meeting after onboarding',
      isStarting: false,
      meetingName: meeting.app.name,
    })

    toast.add({
      analytics: { type: 'first meeting after onboarding', meetingApp: meeting.app.name },
      id: DETECTED_MEETING_TOAST_ID,
      icon: <MeetingIcon appName={meeting.app.name} size={16} />,
      title: meeting.windowTitle || 'Meeting Detected',
      duration: 60_000,
      autoExpandDelayMs: 0,
      autoCollapseDelayMs: 10_000,
      onDismiss: () => {
        meetingToastDismissedAtRef.current = Date.now()
      },
      body: (toastId) => <MeetingStartToast meeting={meeting} toastId={toastId} firstMeetingAfterOnboarding />,
      notchBody: (toastId) => <NotchMeetingStartToast meeting={meeting} toastId={toastId} />,
    })
  })

  useGlobalEvent('meeting:started:audio-permission-required', (payload: AudioPermissionRequiredPayload) => {
    // Intentionally NOT gated by the notification toggles or the detection
    // cooldown: this is the only UI for a capture the main process is actively
    // blocked on (and waiting up to AUDIO_PERMISSION_WAIT_TIMEOUT_MS for), so
    // suppressing it would leave the user with a silent, stalled recording.
    const { meeting, plan } = payload
    const appName = meeting ? (getDetectedMeetingAppName(meeting) ?? 'Google Meet') : null
    const title = meeting ? getMeetingDataFromMeeting(meeting).title : null

    toast.add({
      analytics: { type: 'audio permission required', meetingApp: appName, hasMeeting: !!meeting },
      id: DETECTED_MEETING_TOAST_ID,
      icon: appName ? <MeetingIcon appName={appName} size={16} /> : <HighlightLogoIcon size={16} />,
      title: title || (meeting ? 'Meeting Detected' : 'Audio permissions required'),
      duration: Infinity,
      autoExpandDelayMs: 0,
      autoCollapseDelayMs: 5000,
      priority: 100,
      onDismiss: () => {
        meetingToastDismissedAtRef.current = Date.now()
      },
      body: (toastId) => <AudioPermissionToast meeting={meeting} plan={plan} toastId={toastId} />,
      notchBody: (toastId) => <NotchAudioPermissionToast meeting={meeting} plan={plan} toastId={toastId} />,
    })
  })

  useGlobalEvent('scheduled-meeting:prepare-soon:force', (meeting: CalendarEvent) => {
    toast.add({
      analytics: { type: 'meeting prepare', forced: true },
      id: getMeetingToastId(meeting.id),
      icon: <CalendarEventIcon event={meeting} size={16} />,
      title: meeting.title,
      duration: 2 * 60 * 1000,
      autoExpandDelayMs: 0,
      autoCollapseDelayMs: 5000,
      body: (toastId) => <MeetingViewToast toastId={toastId} meeting={meeting} />,
      notchBody: (toastId) => <NotchMeetingPrepareToast toastId={toastId} meeting={meeting} />,
    })
  })

  useGlobalEvent('scheduled-meeting:starting-soon:force', (meeting: CalendarEvent) => {
    toast.add({
      analytics: { type: 'meeting starting soon', forced: true },
      id: getMeetingToastId(meeting.id),
      icon: <CalendarEventIcon event={meeting} size={16} />,
      title: meeting.title,
      duration: 3 * 60 * 1000,
      autoExpandDelayMs: 0,
      autoCollapseDelayMs: 5000,
      body: (toastId) => <MeetingStartingSoonToast toastId={toastId} meeting={meeting} force />,
      notchBody: (toastId) => <NotchMeetingStartingSoonToast toastId={toastId} meeting={meeting} force />,
    })
  })

  useGlobalEvent('scheduled-meeting:prepare-soon', (meeting: CalendarEvent) => {
    if (!allowMeetingNotifications) return

    AmplitudeRenderer.trackEvent('Audio Note Toast', {
      type: 'scheduled meeting prepare',
      isStarting: false,
      meetingName: 'Google Meet',
    })

    toast.add({
      analytics: { type: 'meeting prepare', forced: false },
      id: getMeetingToastId(meeting.id),
      icon: <CalendarEventIcon event={meeting} size={16} />,
      title: meeting.title,
      duration: 2 * 60 * 1000,
      autoExpandDelayMs: 0,
      autoCollapseDelayMs: 5000,
      body: (toastId) => <MeetingViewToast toastId={toastId} meeting={meeting} />,
      notchBody: (toastId) => <NotchMeetingPrepareToast toastId={toastId} meeting={meeting} />,
    })
  })

  useGlobalEvent('scheduled-meeting:starting-soon', (meeting: CalendarEvent) => {
    if (!allowMeetingNotifications || !meetingStartingSoonEnabled) return

    AmplitudeRenderer.trackEvent('Audio Note Toast', {
      type: 'scheduled meeting',
      isStarting: false,
      meetingName: 'Google Meet',
    })

    toast.add({
      analytics: { type: 'meeting starting soon', forced: false },
      id: getMeetingToastId(meeting.id),
      icon: <CalendarEventIcon event={meeting} size={16} />,
      title: meeting.title,
      duration: 3 * 60 * 1000,
      autoExpandDelayMs: 0,
      autoCollapseDelayMs: 5000,
      body: (toastId) => <MeetingStartingSoonToast toastId={toastId} meeting={meeting} />,
      notchBody: (toastId) => <NotchMeetingStartingSoonToast toastId={toastId} meeting={meeting} />,
    })
  })

  // The billing gate refused a start on a v2 tier; the panel never opens on
  // its own for it (see resolveMeetingRefusalSurface). Same id as the
  // recording-started toast, which already shows the out-of-hours content, so
  // the refusal refreshes that toast instead of stacking a second. Everyone
  // else gets the meeting paywall AssistantEventHandler opens.
  useGlobalEvent(MEETING_LIMIT_REACHED_EVENT, ({ reason }: MeetingLimitReachedPayload) => {
    const surface = resolveMeetingRefusalSurface(reason, meetingLimitRoute)
    if (surface === 'paywall') return
    const refusal =
      surface === 'hours-toast'
        ? {
            analytics: 'meeting hours limit',
            body: (toastId: string) => (
              <OutOfMeetingHoursToast toastId={toastId} calendarEvent={currentMeetingCalendarEvent} />
            ),
          }
        : {
            analytics: 'meeting plan unverified',
            body: (toastId: string) => <PlanUnverifiedToast toastId={toastId} />,
          }
    // The plan the server couldn't see may have lapsed; the next try should know.
    if (surface === 'plan-unverified-toast') {
      refreshBilling()
    }
    toast.add({
      analytics: { type: refusal.analytics },
      id: DETECTED_MEETING_TOAST_ID,
      icon: <HighlightLogoIcon size={16} />,
      title: 'Transcription not started',
      duration: 15000,
      autoExpandDelayMs: 0,
      autoCollapseDelayMs: 8000,
      body: refusal.body,
    })
  })

  // A recording was downgraded to local transcription by a cloud-session
  // outage; billing gates refuse the start with a paywall instead.
  // Deliberately not gated by the notification
  // toggles: this is the only surface telling the user their notes are lower
  // quality and how to restore cloud transcription.
  useGlobalEvent(MEETING_TRANSCRIPTION_DOWNGRADED_EVENT, () => {
    toast.add({
      analytics: { type: 'transcription downgraded' },
      id: 'transcription-downgraded',
      icon: <HighlightLogoIcon size={16} />,
      title: 'Using local transcription',
      duration: 15000,
      autoExpandDelayMs: 0,
      autoCollapseDelayMs: 8000,
      body: (toastId) => <TranscriptionDowngradedToast toastId={toastId} />,
    })
  })

  useGlobalEvent('meeting:awaiting-join', (meeting: CalendarEvent) => {
    toast.add({
      analytics: { type: 'awaiting meeting join' },
      id: getMeetingToastId(meeting.id),
      icon: <CalendarEventIcon event={meeting} size={16} />,
      title: meeting.title,
      duration: Infinity,
      autoExpandDelayMs: 0,
      autoCollapseDelayMs: 5000,
      onDismiss: () => AudioNoteIpcRenderer.cancelAwaitingMeetingJoin(),
      body: (toastId) => <WaitingForMeetingToast meeting={meeting} toastId={toastId} />,
    })
  })

  useGlobalEvent('meeting:awaiting-join-recording-started', (meeting: CalendarEvent) => {
    toast.dismiss(getMeetingToastId(meeting.id))

    toast.add({
      analytics: { type: 'transcription started', joinedFromCalendar: true, outOfHours },
      id: `${getMeetingToastId(meeting.id)}-recording-started`,
      icon: <CalendarEventIcon event={meeting} size={16} />,
      title: meeting.title,
      duration: outOfHours ? 15000 : 5000,
      autoExpandDelayMs: 0,
      autoCollapseDelayMs: 8000,
      body: (toastId) => <RecordingStartedToast toastId={toastId} meeting={meeting} />,
    })
  })
}
